import { ixToast } from "@shared/src/utils/ixToast";

// Types
type BeforeInstallPromptEvent = Event & {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
};

type PWAState = {
  isInstallable: boolean;
  isInstalled: boolean;
  deferredPrompt: BeforeInstallPromptEvent | null;
  lastPromptDate: string | null;
};

type PWAStorageState = {
  lastPromptDate: string | null;
  lastInstalledNoticeDate: string | null;
};

const STORAGE_KEY = "pwa-state";
const PROMPT_COOLDOWN_DAYS = 7;
const INSTALLED_NOTICE_COOLDOWN_DAYS = 2;

// State management
let pwaState: PWAState = {
  isInstallable: false,
  isInstalled: false,
  deferredPrompt: null,
  lastPromptDate: null,
};

const listeners = new Set<(state: PWAState) => void>();

const loadStoredState = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const state = JSON.parse(stored) as PWAStorageState;
      console.log("Loaded stored state:", state);
      return state;
    }
  } catch (error) {
    console.error("Error loading stored state:", error);
  }
  return { lastPromptDate: null, lastInstalledNoticeDate: null };
};

const savePromptState = () => {
  try {
    const currentState = loadStoredState();
    const newState = {
      ...currentState,
      lastPromptDate: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
    console.log("Saved prompt state:", newState);
  } catch (error) {
    console.error("Error saving prompt state:", error);
  }
};

export const saveInstalledNoticeState = () => {
  try {
    const currentState = loadStoredState();
    const newState = {
      ...currentState,
      lastInstalledNoticeDate: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
    console.log("Saved installed notice state:", newState);
  } catch (error) {
    console.error("Error saving installed notice state:", error);
  }
};

const isPromptAllowed = (): boolean => {
  const { lastPromptDate } = loadStoredState();
  if (!lastPromptDate) {
    console.log("No previous prompt date found, allowed to show");
    return true;
  }

  const lastPrompt = new Date(lastPromptDate);
  const now = new Date();
  const diffDays = (now.getTime() - lastPrompt.getTime()) / (1000 * 60 * 60 * 24);

  console.log("Prompt state check:", {
    lastPrompt: lastPromptDate,
    daysSinceLastPrompt: diffDays,
    cooldownDays: PROMPT_COOLDOWN_DAYS,
    isAllowed: diffDays >= PROMPT_COOLDOWN_DAYS,
  });

  return diffDays >= PROMPT_COOLDOWN_DAYS;
};

export const isInstalledNoticeAllowed = (): boolean => {
  const { lastInstalledNoticeDate } = loadStoredState();
  if (!lastInstalledNoticeDate) {
    console.log("No previous installed notice date found, allowed to show");
    return true;
  }

  const lastNotice = new Date(lastInstalledNoticeDate);
  const now = new Date();
  const diffDays = (now.getTime() - lastNotice.getTime()) / (1000 * 60 * 60 * 24);

  console.log("Installed notice state check:", {
    lastNotice: lastInstalledNoticeDate,
    daysSinceLastNotice: diffDays,
    cooldownDays: INSTALLED_NOTICE_COOLDOWN_DAYS,
    isAllowed: diffDays >= INSTALLED_NOTICE_COOLDOWN_DAYS,
  });

  return diffDays >= INSTALLED_NOTICE_COOLDOWN_DAYS;
};

const setState = (updates: Partial<PWAState>) => {
  console.log("Updating PWA state:", updates);
  pwaState = { ...pwaState, ...updates };
  listeners.forEach(listener => listener(pwaState));
};

// Set up early event capture
if (typeof window !== "undefined") {
  console.log("==== Setting up early PWA event capture ====");

  // Load initial state from storage
  const storedState = loadStoredState();
  pwaState.lastPromptDate = storedState.lastPromptDate;

  window.addEventListener("beforeinstallprompt", (e: Event) => {
    console.log("==== Early BeforeInstallPrompt Event Captured ====");
    e.preventDefault();
    const promptEvent = e as BeforeInstallPromptEvent;

    // Only set as installable if we're allowed to prompt
    const canPrompt = isPromptAllowed();
    console.log("Can show prompt:", canPrompt);

    setState({
      deferredPrompt: promptEvent,
      isInstallable: canPrompt,
    });
  });

  window.addEventListener("appinstalled", () => {
    console.log("==== Early App Installed Event Captured ====");
    setState({
      isInstalled: true,
      isInstallable: false,
      deferredPrompt: null,
    });
  });
}

export const subscribe = (listener: (state: PWAState) => void) => {
  listeners.add(listener);
  listener(pwaState);
  return () => listeners.delete(listener);
};

export const getState = () => pwaState;

// Core functionality
export const checkInstallationStatus = () => {
  if (typeof window === "undefined") return false;

  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone ||
    document.referrer.includes("android-app://");

  console.log("==== Installation Status Check ====");
  console.log("Display Mode Standalone:", window.matchMedia("(display-mode: standalone)").matches);
  console.log("Navigator Standalone:", (window.navigator as any).standalone);
  console.log("Android App Referrer:", document.referrer.includes("android-app://"));
  console.log("Final isStandalone:", isStandalone);

  return isStandalone;
};

export const handlePromptDismissal = () => {
  console.log("User dismissed or rejected install prompt");
  savePromptState();
  setState({
    isInstallable: false,
    deferredPrompt: null,
  });
};

const isEmbeddedBrowser = () => {
  if (typeof window === "undefined") return false;

  // Check if running in iframe
  const isIframe = window.self !== window.top;
  // Check if running in Telegram
  const isTelegram = /Telegram/i.test(navigator.userAgent);
  // Check if running in other webviews
  const isWebview = /Android.*Version\/[0-9].[0-9].*Chrome.*Mobile.*Safari/i.test(navigator.userAgent);

  return isIframe || isTelegram || isWebview;
};

const isIOSSafari = () => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua);
  const isChrome = /CriOS/.test(ua);

  return isIOS && isSafari && !isChrome;
};

export const initializePWA = async () => {
  console.log("==== Initializing PWA ====");

  if (typeof window === "undefined") return;

  // Don't initialize in embedded browsers
  if (isEmbeddedBrowser()) {
    console.log("PWA initialization skipped - running in embedded browser");
    setState({ isInstallable: false, isInstalled: false });
    return;
  }

  try {
    // Set initial state
    const installed = checkInstallationStatus();
    setState({ isInstalled: installed });

    // Register service worker with error handling
    if ("serviceWorker" in navigator) {
      try {
        let registration = await navigator.serviceWorker.getRegistration();
        if (!registration) {
          registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
            scope: "/",
          });
        }
        // Add update handling
        registration?.addEventListener("updatefound", () => {
          const newWorker = registration?.installing;
          if (newWorker) {
            newWorker.addEventListener("statechange", () => {
              if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                console.log("New content available, refresh needed");
                // You could trigger a refresh notification here
              }
            });
          }
        });

        // Handle service worker updates
        navigator?.serviceWorker?.addEventListener?.("controllerchange", () => {
          console.log("Service Worker controller changed");
        });

        console.log("Service Worker registered:", {
          scope: registration.scope,
          active: registration.active?.state,
        });
      } catch (error) {
        console.error("Service Worker registration failed:", error);
      }
    }

    // Set up display mode change detection with cleanup
    const mediaQuery = window.matchMedia("(display-mode: standalone)");
    const handleChange = (e: MediaQueryListEvent) => handleDisplayModeChange(e);
    mediaQuery.addEventListener("change", handleChange);

    console.log("PWA initialized successfully");

    // Return cleanup function
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  } catch (error) {
    console.error("PWA initialization failed:", error);
  }
};

export const promptInstall = async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
  console.log("==== Install Prompt Check ====");

  // Block installation in embedded browsers
  if (isEmbeddedBrowser()) {
    console.log("Installation blocked in embedded browser");
    return "unavailable";
  }

  // Special handling for iOS Safari
  if (isIOSSafari()) {
    console.log("iOS Safari detected - showing Add to Home Screen instructions");
    ixToast.info("Tap the share button and select 'Add to Home Screen' to install", {
      title: "Install on iOS",
      position: "bottom-right",
    });
    return "unavailable";
  }

  if (!pwaState.deferredPrompt) {
    console.log("No deferred prompt available");
    return "unavailable";
  }

  try {
    console.log("Showing install prompt...");
    await pwaState.deferredPrompt.prompt();
    const choiceResult = await pwaState.deferredPrompt.userChoice;
    console.log("User choice:", choiceResult.outcome);

    handlePromptDismissal();
    return choiceResult.outcome;
  } catch (error) {
    console.error("Error showing install prompt:", error);
    return "unavailable";
  }
};

const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
  console.log("==== BeforeInstallPrompt Event ====");
  e.preventDefault();

  setState({
    deferredPrompt: e,
    isInstallable: true,
  });

  console.log("Prompt stored and state updated");
};

const handleAppInstalled = () => {
  console.log("==== App Installed Event ====");
  savePromptState();
  setState({
    isInstalled: true,
    isInstallable: false,
    deferredPrompt: null,
  });
};

const handleDisplayModeChange = (e: MediaQueryListEvent) => {
  console.log("Display mode changed:", e.matches);
  if (e.matches) {
    setState({
      isInstalled: true,
      isInstallable: false,
    });
  }
};
